import React from 'react';

/**
 * NeurologicalSection Component
 * Complete neurological assessment for lumbar spine
 * Includes dermatomal patterns, motor/sensory/reflex testing
 */
const NeurologicalSection = ({ config, formData, onChange }) => {
  const handleFieldChange = (fieldId, value) => {
    onChange(fieldId, value);
  };

  const renderField = (field) => {
    const value = formData[field.id] || '';
    const isUrgent = field.urgent;

    return (
      <div 
        key={field.id}
        className={`field-group ${isUrgent ? 'urgent-field' : ''}`}
      >
        <label className="field-label">
          {field.label}
          {field.required && <span className="required">*</span>}
          {isUrgent && <span className="urgent-badge">URGENT</span>}
        </label>

        {field.type === 'textarea' && (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            rows={field.rows || 3}
            className="form-textarea"
          />
        )}

        {field.type === 'text' && (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            className="form-input"
          />
        )}

        {field.type === 'radio' && (
          <div className="radio-group">
            {field.options.map(option => (
              <label key={option} className="radio-option">
                <input
                  type="radio"
                  name={field.id}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleFieldChange(field.id, e.target.value)}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        )}

        {field.note && (
          <div className="field-note">
            <span className="note-icon">ℹ️</span>
            {field.note}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="neurological-section">
      <style jsx>{`
        .neurological-section {
          border: 3px solid #8b5cf6;
          border-radius: 8px;
          padding: 24px;
          background: #faf5ff;
          margin: 24px 0;
        }

        .section-header {
          background: #f3e8ff;
          border-left: 4px solid #8b5cf6;
          padding: 16px;
          border-radius: 6px;
          margin-bottom: 24px;
        }

        .section-header h3 {
          color: #6b21a8;
          font-size: 18px;
          font-weight: 700;
          margin: 0 0 8px 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .section-header p {
          color: #7c3aed;
          margin: 0;
          font-size: 14px;
        }

        .dermatomal-guide {
          background: white;
          padding: 16px;
          border-radius: 6px;
          margin-bottom: 20px;
          border: 2px solid #e9d5ff;
        }

        .dermatomal-guide h4 {
          color: #6b21a8;
          font-size: 15px;
          font-weight: 600;
          margin: 0 0 12px 0;
        }

        .dermatomal-list {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 12px;
        }

        .dermatome-item {
          padding: 10px 12px;
          background: #faf5ff;
          border-radius: 4px;
          border-left: 3px solid #8b5cf6;
        }

        .dermatome-level {
          font-weight: 700;
          color: #6b21a8;
          font-size: 14px;
        }

        .dermatome-distribution {
          font-size: 13px;
          color: #374151;
          margin-top: 4px;
        }

        .field-group {
          background: white;
          padding: 16px;
          border-radius: 6px;
          margin-bottom: 16px;
          border: 1px solid #e5e7eb;
        }

        .urgent-field {
          border-left: 4px solid #dc2626;
          background: #fef2f2;
          border-color: #fecaca;
        }

        .field-label {
          display: block;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 12px;
          font-size: 15px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 8px;
        }

        .required {
          color: #dc2626;
          margin-left: 4px;
        }

        .urgent-badge {
          background: #fee2e2;
          color: #991b1b;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .form-input,
        .form-textarea {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          font-family: inherit;
          transition: border-color 0.2s;
        }

        .form-input:focus,
        .form-textarea:focus {
          outline: none;
          border-color: #8b5cf6;
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
        }

        .form-textarea {
          resize: vertical;
          line-height: 1.5;
        }

        .radio-group {
          display: flex;
          gap: 24px;
          flex-wrap: wrap;
        }

        .radio-option {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          padding: 8px 12px;
          border-radius: 4px;
          transition: background 0.2s;
        }

        .radio-option:hover {
          background: #f9fafb;
        }

        .radio-option input[type="radio"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .radio-option span {
          font-size: 15px;
          color: #374151;
        }

        .field-note {
          margin-top: 10px;
          padding: 10px 12px;
          background: #f0f9ff;
          border-radius: 4px;
          font-size: 13px;
          color: #0c4a6e;
          display: flex;
          align-items: start;
          gap: 8px;
          border-left: 3px solid #0ea5e9;
        }

        .note-icon {
          font-size: 14px;
          flex-shrink: 0;
        }

        .warning-box {
          background: #fef3c7;
          border: 2px solid #f59e0b;
          border-radius: 6px;
          padding: 14px;
          margin-top: 20px;
          display: flex;
          align-items: start;
          gap: 10px;
        }

        .warning-icon {
          font-size: 20px;
          flex-shrink: 0;
        }

        .warning-text {
          font-size: 14px;
          color: #78350f;
          line-height: 1.5;
        }

        @media (max-width: 640px) {
          .neurological-section {
            padding: 16px;
          }

          .dermatomal-list {
            grid-template-columns: 1fr;
          }

          .radio-group {
            flex-direction: column;
            gap: 12px;
          }
        }
      `}</style>

      <div className="section-header">
        <h3>
          <span>🧠</span>
          Complete Neurological Assessment
        </h3>
        <p>Critical for nerve root localization and detecting progressive deficits</p>
      </div>

      <div className="dermatomal-guide">
        <h4>📍 Dermatomal Distribution Guide</h4>
        <div className="dermatomal-list">
          <div className="dermatome-item">
            <div className="dermatome-level">L4 Distribution:</div>
            <div className="dermatome-distribution">
              Inner shin, medial ankle, medial foot
            </div>
          </div>
          <div className="dermatome-item">
            <div className="dermatome-level">L5 Distribution:</div>
            <div className="dermatome-distribution">
              Outer calf, top of foot, big toe, first web space
            </div>
          </div>
          <div className="dermatome-item">
            <div className="dermatome-level">S1 Distribution:</div>
            <div className="dermatome-distribution">
              Sole of foot, little toes, back of calf, lateral foot
            </div>
          </div>
        </div>
      </div>

      <div className="fields-container">
        {config.fields && config.fields.map(field => renderField(field))}
      </div>

      <div className="warning-box">
        <span className="warning-icon">⚠️</span>
        <div className="warning-text">
          <strong>Important:</strong> Progressive or bilateral neurological symptoms require urgent assessment. 
          Document the exact distribution and timing of symptom onset.
        </div>
      </div>
    </div>
  );
};

export default NeurologicalSection;
